import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Mail, Lock, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success('¡Bienvenido de vuelta!');
      navigate('/home');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA] flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#00BFA6]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#00887A]/10 rounded-full blur-3xl" />

      {/* Logo */}
      <div className="flex flex-col items-center mb-8 animate-slide-up">
        <img 
          src="https://customer-assets.emergentagent.com/job_mercamove/artifacts/ch8knfdf_Move_Logo_wHT.png" 
          alt="Mercapoli Move"
          className="h-16"
        />
      </div>

      {/* Login Card */}
      <Card className="w-full max-w-md card-elevated animate-slide-up" style={{ animationDelay: '0.1s' }}>
        <CardHeader className="text-center pb-2">
          <CardTitle className="text-2xl font-bold text-slate-800 font-['Outfit']">
            Iniciar Sesión
          </CardTitle>
          <CardDescription className="text-slate-500">
            Mueve todo. Mueve inteligente.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">Correo electrónico</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="tu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="pl-12 h-14 bg-white border-slate-200 text-slate-800 placeholder:text-slate-400 rounded-xl focus:border-[#00BFA6] focus:ring-[#00BFA6]/20"
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Contraseña</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pl-12 h-14 bg-white border-slate-200 text-slate-800 placeholder:text-slate-400 rounded-xl focus:border-[#00BFA6] focus:ring-[#00BFA6]/20"
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 btn-primary text-lg"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Entrar
                  <ArrowRight className="ml-2 w-5 h-5" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-slate-500">
              ¿No tienes cuenta?{' '}
              <Link 
                to="/register" 
                className="text-[#00BFA6] hover:text-[#00887A] font-medium transition-colors"
                data-testid="register-link"
              >
                Regístrate
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <p className="mt-8 text-slate-400 text-sm animate-fade-in" style={{ animationDelay: '0.3s' }}>
        © 2025 Mercapoli Move. Todos los derechos reservados.
      </p>
    </div>
  );
}
